import React, { useState } from "react";
import Image from "next/image";
import { parseHtml } from "@/utils/common";

// Content type inside each accordion item
export type AccordionContent = {
  heading: string;
  text: string;
  image?: string;
};

// Each accordion item
export type AccordionItem = {
  title: string;
  content: AccordionContent[];
  theme?: "blue" | "green";
};

type AccordionProps = {
  items: AccordionItem[];
};

const AccordionCRM: React.FC<AccordionProps> = ({ items }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="w-full md:mx-auto z-40 relative">
      {items.map((item, index) => (
        <div key={index} className={` sm:mb-8 mb-[24px] border-blue-100 last:sm:mb-[0px]   ${
          index % 2 === 2 ? "border-green-100" : "border-blue-100"
                    } bg-white hover:bg-white shadow-none" `}>
          {/* Header */}
          <button
            className={`flex sm:justify-between justify-start text-left sm:items-center overflow-hidden items-center shadow-[0px_38px_37.5px_-20px_#0000001F] relative z-40 w-full sm:px-6  px-[12px] md:py-7  py-[16px] ${
          index % 2 === 1 ? "border-green-100" : "border-blue-100"
                    } border-1 text-heading-4 font-bold text-primary-text text-center sm:text-start transition cursor-pointer `}
            onClick={() => toggleAccordion(index)}
          >
            <span className="max-w-[805px] mr-[12px]">{item.title}</span>

            <span
              className={`inline-block transition-transform duration-300  w-[42px] h-[42px] min-w-[42px] min-h-[42px] sm:w-[80px] sm:h-[80px] sm:min-w-[80px] sm:min-h-[80px] ${
                openIndex === index ? "rotate-90" : "rotate-0"
              }`}
            >
              {item.theme === "blue" ? (
                // ✅ Blue arrow
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 80 80"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M0 0H80V80H0V0Z" fill="#E6F3FD" />
                  <path
                    d="M43.6453 30.8955L52.7503 40.0005L43.6453 49.1055"
                    stroke="#0086E6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M27.25 40L52.495 40"
                    stroke="#0086E6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                // ✅ Green arrow
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 80 80"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M0 80L0 0L80 0V80H0Z" fill="#F3F8E6" />
                  <path
                    d="M43.6453 30.8955L52.7503 40.0005L43.6453 49.1055"
                    stroke="#8ABC00"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M27.25 40L52.495 40"
                    stroke="#8ABC00"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
          </button>

          {/* Content */}
          {openIndex === index && (
            <div className={`border-1 text-gray-600 animate-fadeIn bg-white ${
          index % 2 === 1 ? "border-green-100 border-t-0" : "border-blue-100 border-t-0"
                    } `}>
              <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-[24px] gap-[0] overflow-hidden sm:first:pt-[32px] sm:last:mb-[24px]  ">
                {item.content.map((block, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-4 sm:px-[49px]  px-[12px] sm:py-[25px] first:pt-[24px] py-[16px]   sm:border-r even:border-r-0 relative  first:after:content-[''] 
                  first:after:absolute 
                  first:after:top-0 
                  first:after:right-[-30px] 
                  first:after:left-[99%] 
                  first:after:w-[10px] 
                  first:after:h-[32px] 
                  first:after:bg-white ${
                      index % 2 === 1 ? "border-green-100 sm:border-b-0  border-b-1last:border-b-0 " : "border-blue-100 sm:border-b-0 border-b-1 last:border-b-0"
                    }`} >
                    {/* ✅ Left-side icon */}
                    {block.image && (
                      <div className="flex-shrink-0 w-[24px] h-[24px] mt-1">
                        <Image
                          src={block.image}
                          alt={block.heading}
                          width={24}
                          height={24}
                          className="object-contain"
                        />
                      </div>
                    )}

                    <div className="flex flex-col text-left">
                      <h4 className="font-bold text-heading-6 text-primary-text sm:mb-1 mb-2">
                        {block.heading}
                      </h4>
                      <p className="text-sm text-subtitle-2 text-secondary-text">
                        {parseHtml(block.text)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default AccordionCRM;
